import { useFlags } from "launchdarkly-react-client-sdk";
import { useMemo, useRef, useState } from "react";

import {
  CurrentUserWithAccountsDocument,
  DatawarehouseMigration,
  DwMigrationStatus,
  GetRawViewsDocument,
  PublishedModelsDocument,
  useDatawarehouseMigrationQuery,
} from "@/apollo/types";
import { useToast } from "@/providers/ToastProvider";
import { useSocketEvent } from "@/socket/SocketContext";
import { useApolloClient } from "@apollo/client";

const activeDWMigrationStatuses = [
  DwMigrationStatus.Started,
  DwMigrationStatus.StoppedSyncs,
  DwMigrationStatus.SyncingModels,
  DwMigrationStatus.SyncingTables,
  DwMigrationStatus.SyncingViews,
  DwMigrationStatus.ClearedErrors,
  DwMigrationStatus.DwMigrationDone,
  DwMigrationStatus.ModelMigrationDone,
  DwMigrationStatus.UpdatedConnection,
];

const isActiveMigrationStatus = (status: DwMigrationStatus) => {
  return status != null && activeDWMigrationStatuses.includes(status);
};

export type TableMigrationProgress = {
  datasetId: string;
  tableId: string;
  percentComplete: number;
  totalTables: number;
  currentTableNum: number;
};

export type ViewMigrationProgress = {
  datasetId: string;
  viewId: string;
  totalViews: number;
  currentViewNum: number;
};

export type MigrationError = {
  message: string;
  retrying: boolean;
};

type MigrationState =
  | {
      migration: undefined;
      migrationIsActive: false;
    }
  | {
      migration: DatawarehouseMigration;
      migrationIsActive: true;
    };

export const useCurrentMigration = (): {
  migrationState: MigrationState;
  statusUpdate?: DwMigrationStatus;
  tableMigrationProgress?: TableMigrationProgress;
  viewMigrationProgress?: ViewMigrationProgress;
  error: MigrationError | null;
} => {
  const toast = useToast();
  const { dataWarehouseMigrationEnabled } = useFlags();
  const migrationWasActive = useRef(false);
  const client = useApolloClient();
  const [tableMigrationProgress, setTableMigrationProgress] =
    useState<TableMigrationProgress>();
  const [viewMigrationProgress, setViewMigrationProgress] =
    useState<ViewMigrationProgress>();
  const [error, setError] = useState<MigrationError | null>(null);

  const { data, refetch } = useDatawarehouseMigrationQuery({
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "network-only",
    skip: !dataWarehouseMigrationEnabled,
    onCompleted: (data) => {
      const migration = data?.datawarehouseMigration;
      if (!migration) return;

      if (isActiveMigrationStatus(migration.status)) {
        migrationWasActive.current = true;
      } else {
        if (migrationWasActive.current) {
          if (migration.status === DwMigrationStatus.Failed) {
            // Migration failed while active, show an error
            toast(
              "Migration failed",
              migration?.errorMessage ?? "Something went wrong.",
              "error",
            );
          }
          if (migration.status === DwMigrationStatus.Completed) {
            toast(
              "Migration completed",
              "Your data warehouse migration has completed.",
              "success",
            );

            //Reload models since they are affected by the last step of the migration
            client.refetchQueries({
              include: [
                PublishedModelsDocument,
                GetRawViewsDocument,
                CurrentUserWithAccountsDocument,
              ],
            });
          }
        }
        migrationWasActive.current = false;
      }
    },
  });

  useSocketEvent("data-warehouse-migration:started", {
    async onMessage() {
      refetch();
    },
  });
  useSocketEvent("data-warehouse-migration:started", {
    async onMessage() {
      refetch();
    },
  });

  useSocketEvent("data-warehouse-migration:status-updated", {
    onMessage(_resp) {
      setError(null);
      refetch();
    },
  });

  useSocketEvent("data-warehouse-migration:table-progress", {
    onMessage(resp) {
      setTableMigrationProgress((prev) => {
        if (
          resp.payload.tableId === prev?.tableId &&
          resp.payload.datasetId === prev?.datasetId &&
          resp.payload.percentComplete < prev?.percentComplete
        ) {
          return prev; // Ignore progress updates that go backwards
        }
        return resp.payload;
      });
    },
  });

  useSocketEvent("data-warehouse-migration:view-progress", {
    onMessage(resp) {
      setViewMigrationProgress(resp.payload);
    },
  });

  useSocketEvent("data-warehouse-migration:failed-retrying", {
    onMessage(resp) {
      setError({ message: resp.payload.errorMessage, retrying: true });
      refetch();
    },
  });

  useSocketEvent("data-warehouse-migration:failed", {
    onMessage() {
      refetch();
    },
  });

  const migrationState: MigrationState = useMemo(() => {
    if (
      data?.datawarehouseMigration != null &&
      isActiveMigrationStatus(data.datawarehouseMigration.status)
    ) {
      return {
        migration: data.datawarehouseMigration as DatawarehouseMigration,
        migrationIsActive: true,
      };
    }
    return {
      migration: undefined,
      migrationIsActive: false,
    };
  }, [data?.datawarehouseMigration]);

  return {
    migrationState,
    tableMigrationProgress,
    viewMigrationProgress,
    error,
  };
};
